import { lazy, Suspense, useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { api, clearSession, getAccessToken, loadStoredUser, saveSession } from "./apiClient.js";
import { Toast } from "./components/v020/ui.jsx";
import { I18nLegacyBridge, I18nProvider, useI18n } from "./i18n.jsx";

const CentralLoginRouter = lazy(() => import("./components/v020/CentralLoginRouter.jsx"));
const DashboardShell = lazy(() => import("./components/v020/DashboardShell.jsx"));
const InvitationAcceptPage = lazy(() => import("./components/v020/InvitationAcceptPage.jsx"));
const StreetDisplayPage = lazy(() => import("./components/v020/StreetDisplayPage.jsx"));
const InitialSetupWizard = lazy(() => import("./components/setup/InitialSetupWizard.jsx"));

function AppInner() {
  const [toast, setToast] = useState({ message: "", kind: "info" });
  const [sessionUser, setSessionUser] = useState(() => loadStoredUser());
  const [sessionChecked, setSessionChecked] = useState(false);
  const [setupStatus, setSetupStatus] = useState(null);
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem("lenox_theme");
    if (stored === "dark" || stored === "light") return stored;
    return "dark";
  });

  useEffect(() => {
    const dark = theme === "dark";
    document.documentElement.classList.toggle("theme-dark", dark);
    document.body.classList.toggle("theme-dark", dark);
    document.documentElement.style.colorScheme = dark ? "dark" : "light";
    localStorage.setItem("lenox_theme", theme);
  }, [theme]);

  useEffect(() => {
    let alive = true;
    async function verifySession() {
      try {
        const setup = await api.setupStatus();
        if (!alive) return;
        setSetupStatus(setup);
        if (setup?.setup_required) {
          clearSession();
          setSessionUser(null);
          setSessionChecked(true);
          return;
        }
      } catch {
        if (alive) setSetupStatus({ setup_required: false, setup_status: "unknown" });
      }
      if (!getAccessToken()) {
        if (alive) { setSessionUser(null); setSessionChecked(true); }
        return;
      }
      try {
        const user = await api.authMe();
        if (alive) {
          localStorage.setItem("lenox_user", JSON.stringify(user));
          setSessionUser(user);
        }
      } catch {
        clearSession();
        if (alive) setSessionUser(null);
      } finally {
        if (alive) setSessionChecked(true);
      }
    }
    verifySession();
    const onCleared = () => setSessionUser(null);
    window.addEventListener("lenox-session-cleared", onCleared);
    return () => { alive = false; window.removeEventListener("lenox-session-cleared", onCleared); };
  }, []);

  function notify(message, kind = "info") {
    setToast({ message, kind });
  }

  function handleLogin(response) {
    const user = saveSession(response);
    setSessionUser(user);
  }

  async function handleLogout() {
    try { await api.logout(); } catch { /* local logout remains valid */ }
    clearSession();
    setSessionUser(null);
  }

  const themeToggle = (
    <button
      type="button"
      title={theme === "dark" ? "Light Mode" : "Dark Mode"}
      onClick={() => setTheme((current) => current === "dark" ? "light" : "dark")}
      className="lenox-theme-toggle fixed bottom-4 right-4 z-[90] flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-[#0b1625] text-cyan-200 shadow-2xl hover:bg-[#142238]"
    >
      {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  );

  const { t } = useI18n();

  if (window.location.pathname.replace(/\/+$/, "") === "/display_street") {
    return <Suspense fallback={<div className="grid min-h-screen place-items-center bg-zinc-950 text-white">{t("common.loading")}</div>}><StreetDisplayPage /></Suspense>;
  }

  if (!sessionChecked) {
    return <div className="grid min-h-screen place-items-center bg-zinc-950 text-sm font-black uppercase tracking-[0.25em] text-white">{t("common.loading")}</div>;
  }

  if (setupStatus?.setup_required) {
    return <Suspense fallback={<div className="grid min-h-screen place-items-center bg-zinc-950 text-white">{t("common.loading")}</div>}><InitialSetupWizard status={setupStatus} onComplete={(response) => { handleLogin(response); setSetupStatus({ ...setupStatus, setup_required: false, setup_status: "completed" }); }} /></Suspense>;
  }

  const inviteMatch = window.location.pathname.match(/^\/invite\/([^/]+)\/?$/);
  if (!sessionUser && inviteMatch) {
    return <Suspense fallback={<div className="grid min-h-screen place-items-center bg-zinc-950 text-white">{t("common.loading")}</div>}><InvitationAcceptPage token={decodeURIComponent(inviteMatch[1])} onDone={() => { window.history.replaceState({}, "", "/"); window.location.reload(); }} /></Suspense>;
  }

  return (
    <>
      <I18nLegacyBridge />
      <Suspense fallback={<div className="grid min-h-screen place-items-center bg-zinc-950 text-sm font-black uppercase tracking-[0.25em] text-white">{t("common.loading")}</div>}>
        {sessionUser
          ? <DashboardShell initialUser={sessionUser} onLogout={handleLogout} />
          : <CentralLoginRouter onLogin={handleLogin} notify={notify} />}
      </Suspense>
      {themeToggle}
      <Toast toast={toast} clear={() => setToast({ message: "", kind: "info" })} />
    </>
  );
}


export default function App() {
  return <I18nProvider><AppInner /></I18nProvider>;
}
